import { ReactElement } from "react";
import styles from "./styles.module.scss";
import { useDomainContext } from "@hooks/useDomainContext";
import { TStandAloneImage } from "@page-builder/ts/generated";
import { ResponsiveImageRenderer } from "@page-builder/utils/index";

export default function StandAloneImage({
    props = {},
}: {
    props?: TStandAloneImage["props"];
}): ReactElement {
    const domainContext = useDomainContext();
    const { tagIcon, tagLabel, showTagIcon } = props;

    // Helper function to get dynamic image
    const getDynamicImage = () => {
        // If no items or no dynamicImageBasedOnPath, return the default image
        if (!props.items || !props.dynamicImageBasedOnPath) {
            return props.image;
        }

        // Parse the path from dynamicImageBasedOnPath (e.g., "visitDetails.stateCode")
        const pathParts = props.dynamicImageBasedOnPath
            .trim()
            .split(".")
            .map((part) => part.trim());

        // Navigate through the domainContext to get the value
        let context: unknown = domainContext;

        for (const part of pathParts) {
            if (context && typeof context === "object" && part in context) {
                context = (context as Record<string, unknown>)[part];
            } else {
                // Path not found, fallback to default image
                return props.image;
            }
        }

        // If we got a string value (e.g., state code), find matching item
        if (typeof context === "string") {
            const contextValue = context;
            const matchedItem = props.items.find(
                (item) =>
                    item.title?.toUpperCase() === contextValue?.toUpperCase(),
            );

            if (matchedItem?.image) {
                return matchedItem.image;
            }
        }

        // Fallback to default image
        return props.image;
    };

    const dynamicImage = getDynamicImage();

    // Determine if we have a valid image source to prevent errors
    if (!dynamicImage) return <></>;

    return (
        <div className={styles["image-container"]}>
            {(tagIcon || tagLabel) && (
                <div className={styles.tag}>
                    {showTagIcon && tagIcon && (
                        <img
                            src={tagIcon}
                            alt={tagLabel || "Tag icon"}
                            className={styles.tagIcon}
                        />
                    )}
                    {tagLabel && (
                        <span className={styles.tagLabel}>{tagLabel}</span>
                    )}
                </div>
            )}
            <ResponsiveImageRenderer
                image={dynamicImage}
                alt={props.imageAlt}
            />
        </div>
    );
}
